import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiV2 from "api-v2";
import {
	Template,
	TemplatesResponse,
	TemplatesPaginatedResponse,
} from "types/template";
import { toFormData } from "axios";
import { grabFirstError, showToast } from "utils/helpers";

export const saveQuestionnareTemplate = async (id: number, name: string) => {
	const formData = {
		rfq_id: id,
		name,
	};
	try {
		const { data: saved } = await apiV2.post(`template/store`, formData);
		if (saved.status) {
			showToast(saved?.message, saved?.title, true);
			return saved.data;
		} else {
			showToast(saved?.message, saved?.title);
			return null;
		}
	} catch (err) {
		showToast("Template saved failed", "Error");
		throw err;
	}
};

export const getTemplateById = async (templateId: number) => {
	try {
		const { data: template } = await apiV2.get(`template/${templateId}`);
		if (template.status) {
			return template.data;
		} else {
			showToast(
				template?.message || "Failed to fetch template",
				template?.title || "Error"
			);
			return null;
		}
	} catch (error) {
		showToast("Failed to fetch template", "Error");
		return null;
	}
};

export const saveRFXTemplateFromExisting = async (
	name: string,
	rfq_id: number
) => {
	try {
		const response = await apiV2.post("/template/store", {
			name,
			rfq_id,
		});
		if (response.status === 200) {
			showToast(response.data.message, response.data.title, true);
			return response.data;
		}
	} catch (error) {
		showToast(
			error?.response?.data?.message || "Failed to save template",
			"Error"
		);
		throw error;
	}
};

export const saveRFXTemplate = async (
	data,
	step = 1,
	isSubmit = false,
	actions
) => {
	const formData = toFormData({ ...data, step, isSubmit }, undefined, {
		indexes: true,
	});
	try {
		const { data: saved } = await apiV2.post(`template/save`, formData);
		if (saved.status) {
			showToast(saved?.message, saved?.title, true);
			return saved.data;
		} else {
			showToast(
				isSubmit ? "Failed to submit template." : "Template saved failed",
				saved?.title
			);
			return null;
		}
	} catch ({ response }) {
		response?.data?.errors &&
			actions &&
			actions.setErrors(response?.data?.errors);
		showToast(
			(isSubmit ? "Failed to submit template. " : "Template saved failed. ") +
				(grabFirstError(response?.data?.errors) || ""),
			response?.data?.title
		);
		throw Error(response);
	}
};

const fetchTemplates = async (page?: number): Promise<TemplatesResponse> => {
	const url = page ? `template?page=${page}` : "template";
	const { data } = await apiV2.get(url);
	if (page) {
		// Return pagination data when page parameter is provided (show all templates including archived)
		return {
			data: data.templates.data || [],
			meta: data.templates.meta,
			links: data.templates.links,
		} as TemplatesPaginatedResponse;
	}
	// Return just the data array when no pagination (filter out archived templates)
	const templates = data.templates.data || [];
	const activeTemplates = templates.filter(
		(template: Template) => template.status !== "archived"
	);
	return activeTemplates as Template[];
};

export const createTemplate = async (companyId: string | number) => {
	const response = await saveRFXTemplate(
		{
			template_name: "New Template",
			returnable_schedule_type: "form",
			quote_type: "form",
			allow_flexible_quote: "1",
			documentation_description: "",
			documents: [],
			sections: [],
			quote: [],
		},
		1,
		false,
		undefined
	);
	return response;
};

export const deleteRFXTemplate = async (templateId: number) => {
	try {
		const { data } = await apiV2.delete(`template/${templateId}/destroy`);
		if (data.status) {
			showToast(data.message, data.title, true);
			return data;
		} else {
			showToast(data.message, data.title);
			return null;
		}
	} catch (error) {
		showToast("Failed to delete template", "Error");
		throw error;
	}
};

export const updateRFXTemplateStatus = async (
	templateId: number,
	status: "archived" | "locked" | "active"
) => {
	try {
		const formData = new FormData();
		formData.append("template_id", templateId.toString());
		formData.append("status", status);

		const { data } = await apiV2.post("template/update-status", formData);
		if (data.status) {
			showToast(data.message, data.title, true);
			return data;
		} else {
			showToast(data.message, data.title);
			return null;
		}
	} catch (error) {
		// For 403 errors, don't show additional toast since interceptor already handles it
		if (error?.response?.status !== 403) {
			showToast("Failed to update template status", "Error");
		}
		throw error;
	}
};

// Legacy function for backward compatibility
const deleteTemplate = async (templateId: number) => {
	await apiV2.delete(`template/${templateId}`);
};

export const useTemplates = (page?: number) => {
	return useQuery({
		queryKey: page ? ["templates", page] : ["templates"],
		queryFn: () => fetchTemplates(page),
	});
};

export const useCreateTemplate = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: createTemplate,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["templates"] });
		},
	});
};

export const useDeleteTemplate = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: deleteTemplate,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["templates"] });
		},
	});
};

export const useSaveQuestionnaireTemplate = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, name }: { id: number; name: string }) =>
			saveQuestionnareTemplate(id, name),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["templates"] });
		},
	});
};

export const useSaveRFXTemplateFromExisting = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ name, rfq_id }: { name: string; rfq_id: number }) =>
			saveRFXTemplateFromExisting(name, rfq_id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["templates"] });
		},
	});
};
